import React from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Platform } from 'react-native';
import { useI18n } from '../../i18n';
import { AgeAnswer, AgeStep } from './ageChoiceLogic';

/** iOS кладёт подчёркивание вплотную к буквам — там линия рамкой снизу, с зазором. */
const IOS = Platform.OS === 'ios';

interface Props {
  step: AgeStep;
  onAnswer: (answer: AgeAnswer) => void;
  textColor: string;
  mutedColor: string;
  accentColor: string;
  borderColor: string;
  /** Smaller tiles for narrow web layouts (RegisterScreen.tsx); off by default. */
  compact?: boolean;
}

/**
 * CORE-1404 — возрастной выбор: «Мне есть 18» / «Мне меньше 18», затем
 * «Мне есть 14» / «Мне меньше 14». После выбора — строка с ответом и «Изменить»;
 * младше 14 — объяснение, что регистрация недоступна.
 */
export const AgeChoice: React.FC<Props> = ({ step, onAnswer, textColor, mutedColor, accentColor, borderColor, compact = false }) => {
  const { t } = useI18n('auth');
  const linkStyle = [styles.link, { color: accentColor }, IOS && [styles.linkIos, { borderBottomColor: accentColor }]];

  if (step.kind === 'done') {
    if (step.ageGroup === 'under14') {
      return (
        <View style={styles.wrap} testID="ageChoiceBlocked">
          <Text style={[styles.question, { color: textColor }]}>{t('age.under14Title')}</Text>
          <Text style={[styles.hint, { color: mutedColor }]}>{t('age.under14Body')}</Text>
          <TouchableOpacity onPress={() => onAnswer('reset')} hitSlop={8} accessibilityRole="button">
            <Text style={linkStyle}>{t('age.change')}</Text>
          </TouchableOpacity>
        </View>
      );
    }
    return (
      <View style={styles.summary} testID="ageChoiceSummary">
        <Text style={[styles.hint, { color: mutedColor }]}>
          {step.ageGroup === 'adult' ? t('age.summaryAdult') : t('age.summaryTeen')}
        </Text>
        <TouchableOpacity onPress={() => onAnswer('reset')} hitSlop={8} accessibilityRole="button">
          <Text style={linkStyle}>{t('age.change')}</Text>
        </TouchableOpacity>
      </View>
    );
  }

  const options: { answer: AgeAnswer; label: string; testID: string }[] =
    step.kind === 'ask18'
      ? [
          { answer: 'is18', label: t('age.is18'), testID: 'ageIs18' },
          { answer: 'below18', label: t('age.below18'), testID: 'ageBelow18' },
        ]
      : [
          { answer: 'is14', label: t('age.is14'), testID: 'ageIs14' },
          { answer: 'below14', label: t('age.below14'), testID: 'ageBelow14' },
        ];

  return (
    <View style={styles.wrap}>
      <Text style={[styles.question, { color: textColor }]}>
        {step.kind === 'ask18' ? t('age.question18') : t('age.question14')}
      </Text>
      <View style={styles.row}>
        {options.map((o) => (
          <TouchableOpacity
            key={o.answer}
            testID={o.testID}
            onPress={() => onAnswer(o.answer)}
            accessibilityRole="button"
            activeOpacity={0.85}
            style={[styles.option, compact && styles.optionCompact, { borderColor }]}
          >
            <Text style={[styles.optionText, compact && styles.optionTextCompact, { color: textColor }]}>{o.label}</Text>
          </TouchableOpacity>
        ))}
      </View>
      {step.kind === 'ask14' && (
        <TouchableOpacity onPress={() => onAnswer('reset')} hitSlop={8} accessibilityRole="button">
          <Text style={linkStyle}>{t('age.back')}</Text>
        </TouchableOpacity>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  wrap: {
    gap: 12,
  },
  question: {
    fontSize: 16,
    fontWeight: '600',
    lineHeight: 22,
  },
  row: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
  },
  option: {
    flexGrow: 1,
    flexBasis: 140,
    borderWidth: 1.5,
    borderRadius: 14,
    paddingVertical: 14,
    paddingHorizontal: 16,
    alignItems: 'center',
  },
  optionText: {
    fontSize: 15,
    fontWeight: '600',
  },
  // compact: two tiles side by side inside a 270px card (360px phone).
  optionCompact: {
    flexBasis: 120,
    borderRadius: 12,
    paddingVertical: 10,
    paddingHorizontal: 8,
  },
  optionTextCompact: {
    fontSize: 14,
  },
  summary: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    flexWrap: 'wrap',
  },
  hint: {
    fontSize: 14,
    lineHeight: 20,
  },
  link: {
    fontSize: 14,
    fontWeight: '600',
    textDecorationLine: 'underline',
  },
  linkIos: {
    textDecorationLine: 'none',
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
});
